/**
 * useSubmissions Hook
 * React Query hooks for submission data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { submissionsAPI } from '../services/api';

// Query keys
const QUERY_KEYS = {
  submissions: (params) => ['submissions', params],
  submission: (id) => ['submission', id]
};

/**
 * Fetch submissions list with filters
 */
export const useSubmissions = (params = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.submissions(params),
    queryFn: () => submissionsAPI.getAll(params),
    staleTime: 0, // Always consider data stale
    cacheTime: 30000 // Keep in cache for 30 seconds
  });
};

/**
 * Fetch single submission by ID
 */
export const useSubmission = (id) => {
  return useQuery({
    queryKey: QUERY_KEYS.submission(id),
    queryFn: () => submissionsAPI.getById(id),
    enabled: !!id,
    staleTime: 0, // Always consider data stale
    cacheTime: 30000 // Keep in cache for 30 seconds
  });
};

/**
 * Accept submission mutation
 */
export const useAcceptSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => submissionsAPI.accept(id, data),
    onSuccess: (data, variables) => {
      // Force refetch instead of just invalidating
      queryClient.refetchQueries({ queryKey: QUERY_KEYS.submission(variables.id) });
      queryClient.refetchQueries({ queryKey: ['submissions'] });
      // Also invalidate games list since a new game was created
      queryClient.invalidateQueries({ queryKey: ['games'] });
    }
  });
};

/**
 * Reject submission mutation
 */
export const useRejectSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => submissionsAPI.reject(id, data),
    onSuccess: (data, variables) => {
      // Force refetch instead of just invalidating
      queryClient.refetchQueries({ queryKey: QUERY_KEYS.submission(variables.id) });
      queryClient.refetchQueries({ queryKey: ['submissions'] });
    }
  });
};

/**
 * Request more info mutation
 */
export const useRequestSubmissionInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => submissionsAPI.requestInfo(id, data),
    onSuccess: (data, variables) => {
      // Force refetch instead of just invalidating
      queryClient.refetchQueries({ queryKey: QUERY_KEYS.submission(variables.id) });
      queryClient.refetchQueries({ queryKey: ['submissions'] });
    }
  });
};

/**
 * Update submission status mutation
 */
export const useUpdateSubmissionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, notes }) => submissionsAPI.updateStatus(id, status, notes),
    onSuccess: (data, variables) => {
      // Force refetch instead of just invalidating
      queryClient.refetchQueries({ queryKey: QUERY_KEYS.submission(variables.id) });
      queryClient.refetchQueries({ queryKey: ['submissions'] });
    }
  });
};
